/**
 * When has this account's entitlement set actually been ANSWERED?
 *
 * `SubscriptionProvider` cannot answer that with the `loading` flag it already has: `loading` is set inside
 * `refreshSubscription`, so before the first request starts it is FALSE — indistinguishable from "resolved".
 * At that moment `allowedUseCases` still holds the `'all'` sentinel and `featureAccess` is `null`, which
 * together describe a real account: unrestricted and unpaid. So the first paint of the dashboard drew every
 * card, most of them locked, each with a "Subscribe to get access" upsell — the flicker the tester caught on
 * a direct `/simulate` load, and a window in which a summit attendee can click a use case their plan forbids.
 *
 * Extracted as a pure function because the two cases that make a naive `!settled` wrong are exactly the two
 * a component author will not think of, and inline in the provider neither is reachable by a test:
 *
 *   - nobody is signed in — `refreshSubscription` returns immediately and never settles anything;
 *   - a MARKETPLACE account — the `/subscription/*` routes 403 those, so the provider deliberately skips the
 *     calls altogether.
 *
 * Both would hold a loading flag forever, and a permanent skeleton is a worse bug than the flicker it
 * replaced. Here each is one assertion.
 */

/** Marketplace accounts pay per study and have no enterprise subscription to resolve. */
export const MARKETPLACE_ACCOUNT_TYPE = 'marketplace';

export interface EntitlementsResolutionInput {
  isAuthenticated: boolean;
  /** `user.account_type`, absent for accounts predating the field. */
  accountType: string | null | undefined;
  /** Has a refresh completed — successfully OR not? A failure is an answer; the fallbacks then apply. */
  firstResolutionSettled: boolean;
}

export const entitlementsAreLoading = ({
  isAuthenticated,
  accountType,
  firstResolutionSettled,
}: EntitlementsResolutionInput): boolean => {
  if (!isAuthenticated) return false;
  if (accountType === MARKETPLACE_ACCOUNT_TYPE) return false;
  return !firstResolutionSettled;
};
